import { useState } from "react";
import type { DeviceTypeFieldDefinition, SecretFieldState } from "../../api/types";
import {
  SECRET_MAX_LENGTH,
  WEEKDAY_NAMES,
  configFieldId,
  describeConfigValue,
  groupFieldsBySection,
  invalidFormattedNumber,
  minuteOfDayToTime,
  secretValueError,
  timeToMinuteOfDay,
  toggleWeekday,
  weekdayMaskDays,
  type ConfigValues,
} from "./configForm";

// Schema-driven renderer for device-type configuration fields. The same
// component backs both the device create form and the per-device shadow
// editor, so the form can never drift from the device-type schema (and
// therefore the managed shadow config) the backend serves. Pure value
// helpers live in ./configForm.

function isFullWidth(field: DeviceTypeFieldDefinition): boolean {
  return field.kind === "textarea" || field.kind === "string_array" || field.kind === "phone_array";
}

function isSelect(field: DeviceTypeFieldDefinition): boolean {
  return Array.isArray(field.options) && field.options.length > 0;
}

function isChoiceSelect(field: DeviceTypeFieldDefinition): boolean {
  return Array.isArray(field.choices) && field.choices.length > 0;
}

// The label an operator sees for the value the firmware falls back to. Matched
// against `choices` so a wire value like "auto_answer_all" is shown as the same
// words as the dropdown entry rather than as a raw enum.
function deviceDefaultLabel(field: DeviceTypeFieldDefinition): string | null {
  if (!field.deviceDefault) return null;
  // A weekday mask or a minute of the day is shown as days or a time, not as
  // the integer the device stores.
  if (isNumberFormat(field)) return describeConfigValue(field, field.deviceDefault);
  const match = field.choices?.find((c) => c.value === field.deviceDefault);
  return match ? match.label : field.deviceDefault;
}

function isNumberFormat(field: DeviceTypeFieldDefinition): boolean {
  return field.kind === "number" && (field.format === "weekday_mask" || field.format === "minute_of_day");
}

function isSlider(field: DeviceTypeFieldDefinition, preferNumberInput: boolean): boolean {
  return !preferNumberInput && field.kind === "number" && field.min !== undefined && field.max !== undefined;
}

const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-950 dark:text-white";

interface SecretInputProps {
  field: DeviceTypeFieldDefinition;
  id: string;
  value: string;
  onChange: (value: string) => void;
  state?: SecretFieldState;
  canReveal: boolean;
  // The stored value, for a page that does not preload it into the box. It is
  // only shown, read-only, while revealed; the box stays for a new value.
  storedValue?: string;
}

// A secret is always masked. Only a caller who may read secrets
// (tenant_admin and above) gets a Reveal toggle; anyone else sees whether one
// is set and can type a replacement, never the stored value (the API does not
// send it to them). Leaving the box empty keeps the stored secret.
function SecretInput({ field, id, value, onChange, state, canReveal, storedValue }: SecretInputProps) {
  const [revealed, setRevealed] = useState(false);
  const show = canReveal && revealed;
  const isSet = state?.isSet ?? value !== "";
  // The device reports one but the platform holds none (e.g. a password changed
  // on the device that was not adopted). "Not set" would be wrong: the device
  // is protected, just not by a value the platform knows.
  const onDeviceOnly = !isSet && state?.reportedIsSet === true;
  const error = secretValueError(value);
  // The box can be empty for a set secret (nothing typed yet), so a caller who
  // may reveal it is told the same as anyone else: blank keeps it.
  const placeholder = canReveal
    ? isSet
      ? "Set — type a new password to replace it"
      : onDeviceOnly
        ? "Set on device only"
        : "Not set"
    : isSet
      ? "Set — type a new password to replace it"
      : onDeviceOnly
        ? "Set on device only — type a password to set it here"
        : "Not set — type a password to set it";
  const statusLabel = isSet ? "Set" : onDeviceOnly ? "Set on device only" : "Not set";
  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type={show ? "text" : "password"}
          // Stops the browser offering the operator's own login for this box.
          autoComplete="new-password"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={SECRET_MAX_LENGTH}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          className={inputClass}
        />
        {canReveal && (
          <button
            type="button"
            onClick={() => setRevealed((current) => !current)}
            aria-controls={id}
            aria-pressed={show}
            className="shrink-0 rounded-md border border-gray-300 px-2.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
          >
            {show ? "Hide" : "Reveal"}
          </button>
        )}
        {!canReveal && (
          <span
            data-testid={`${id}-secret-status`}
            className={
              isSet
                ? "inline-flex shrink-0 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-300"
                : onDeviceOnly
                  ? "inline-flex shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                  : "inline-flex shrink-0 rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-semibold text-gray-700 dark:bg-riot-navy-700 dark:text-riot-navy-100"
            }
          >
            {statusLabel}
          </span>
        )}
      </div>
      {show && storedValue && (
        <p className="mt-1 text-[11px] text-gray-600 dark:text-riot-navy-200" data-testid={`${id}-secret-stored`}>
          Current: <span className="font-mono select-all">{storedValue}</span>
        </p>
      )}
      {error && (
        <p className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400" data-testid={`${id}-secret-error`}>
          {field.label}: {error}
        </p>
      )}
      {state?.pending && (
        <p className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-400" data-testid={`${id}-secret-pending`}>
          Not on the device yet.
        </p>
      )}
    </div>
  );
}

interface WeekdayMaskInputProps {
  field: DeviceTypeFieldDefinition;
  // The group is named by the field's own label, rendered with id `${id}-label`.
  id: string;
  value: string;
  onChange: (value: string) => void;
}

// InvalidFormattedValue shows a stored weekday_mask / minute_of_day value the
// control cannot represent, as it is, so it is not mistaken for "Off" or an
// empty time. Choosing days or a time replaces it.
function InvalidFormattedValue({ id, value }: { id: string; value: string }) {
  return (
    <span data-testid={`${id}-invalid`} className="text-xs font-medium text-red-600 dark:text-red-400">
      {value.trim()} (invalid)
    </span>
  );
}

// A weekday_mask number as one checkbox per day, Sunday first (bit 0). With no
// day ticked the value is 0, which the device reads as off.
function WeekdayMaskInput({ field, id, value, onChange }: WeekdayMaskInputProps) {
  const days = weekdayMaskDays(value);
  const invalid = invalidFormattedNumber(field, value);
  const none = days.every((on) => !on);
  return (
    <div id={id} role="group" aria-labelledby={`${id}-label`} className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {WEEKDAY_NAMES.map((name, day) => {
        const dayId = `${id}-${name.toLowerCase()}`;
        return (
          <label key={name} htmlFor={dayId} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-riot-navy-200">
            <input
              id={dayId}
              type="checkbox"
              checked={days[day]}
              onChange={(e) => onChange(toggleWeekday(value, day, e.target.checked))}
              className="h-4 w-4 rounded border-gray-300 text-riot-gold-500 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-950"
            />
            <span>{name}</span>
          </label>
        );
      })}
      {/* A blank value is not 0: it is left out of a save and the device keeps
          its own (by default Sundays). Only a real 0 is off. */}
      {invalid && <InvalidFormattedValue id={id} value={value} />}
      {none &&
        !invalid &&
        (value.trim() === "" ? (
          <span data-testid={`${id}-unset`} className="text-xs font-medium text-gray-400 dark:text-riot-navy-400">
            Not set
          </span>
        ) : (
          <span data-testid={`${id}-off`} className="text-xs font-medium text-gray-500 dark:text-riot-navy-400">
            Off
          </span>
        ))}
    </div>
  );
}

interface FieldControlProps {
  field: DeviceTypeFieldDefinition;
  id: string;
  value: string;
  onChange: (value: string) => void;
  preferNumberInput: boolean;
  secretState?: SecretFieldState;
  canRevealSecrets: boolean;
  storedSecret?: string;
}

function FieldControl({
  field,
  id,
  value,
  onChange,
  preferNumberInput,
  secretState,
  canRevealSecrets,
  storedSecret,
}: FieldControlProps) {
  if (field.kind === "secret") {
    return (
      <SecretInput
        field={field}
        id={id}
        value={value}
        onChange={onChange}
        state={secretState}
        canReveal={canRevealSecrets}
        storedValue={canRevealSecrets ? storedSecret : undefined}
      />
    );
  }
  // Before the dropdown, slider and number branches: these are numbers, but
  // shown as days or a time of day.
  if (field.kind === "number" && field.format === "weekday_mask") {
    return <WeekdayMaskInput field={field} id={id} value={value} onChange={onChange} />;
  }
  if (field.kind === "number" && field.format === "minute_of_day") {
    const timeInput = (
      <input
        id={id}
        type="time"
        step={60}
        value={minuteOfDayToTime(value)}
        onChange={(e) => onChange(timeToMinuteOfDay(e.target.value))}
        className={inputClass}
      />
    );
    if (!invalidFormattedNumber(field, value)) return timeInput;
    return (
      <div className="flex flex-col gap-1">
        {timeInput}
        <InvalidFormattedValue id={id} value={value} />
      </div>
    );
  }
  if (field.kind === "textarea") {
    return (
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        maxLength={field.max}
        placeholder={field.placeholder}
        className={inputClass}
      />
    );
  }
  if (field.kind === "string_array" || field.kind === "phone_array") {
    return (
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        placeholder={field.placeholder || (field.kind === "phone_array" ? "One number per line" : "One value per line")}
        className={inputClass}
      />
    );
  }
  if (isSelect(field)) {
    const options = field.options!;
    const hasMatch = options.some((option) => String(option.value) === value);
    return (
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
        {value === "" && (
          <option value="" disabled>
            Select a level…
          </option>
        )}
        {/* A stored value outside the curated set (e.g. set via API before
            the dropdown existed) is shown as-is so it round-trips unchanged
            rather than silently snapping to another level. */}
        {value !== "" && !hasMatch && <option value={value}>{`Custom (${value})`}</option>}
        {options.map((option) => (
          <option key={String(option.value)} value={String(option.value)}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }
  if (isChoiceSelect(field)) {
    const choices = field.choices!;
    const hasMatch = choices.some((choice) => choice.value === value);
    return (
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
        {value === "" && (
          <option value="" disabled>
            Select…
          </option>
        )}
        {/* Same reasoning as the numeric dropdown: a stored value outside the
            curated set is shown as-is so it round-trips unchanged instead of
            silently snapping to another policy. */}
        {value !== "" && !hasMatch && <option value={value}>{`Custom (${value})`}</option>}
        {choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
    );
  }
  if (isSlider(field, preferNumberInput)) {
    // A range input must carry a number, but "no stored value" is not the same
    // as "the minimum". Resting the handle on field.min rendered an unset field
    // as a real reading at the bottom of its scale. For the since-retired
    // `aec_delay_samples` — deliberately unseeded, measured per installation — that read
    // as -2400 samples: indistinguishable from a catastrophically misaligned
    // echo canceller, and one stray drag away from actually writing it.
    //
    // Rest on the firmware's own fallback where the field declares one, else
    // the midpoint, and label it "Not set" so the number is never mistaken for
    // the device's. buildConfigOverrides still keys off the empty form value,
    // so an untouched field is deleted from the payload rather than saved.
    const isUnset = value === "";
    const deviceDefaultNumber = Number(field.deviceDefault);
    const restingValue = Number.isFinite(deviceDefaultNumber)
      ? deviceDefaultNumber
      : Math.round(((field.min as number) + (field.max as number)) / 2);
    return (
      <div className="flex items-center gap-3">
        <input
          id={id}
          type="range"
          min={field.min}
          max={field.max}
          step={field.step ?? 1}
          value={isUnset ? restingValue : value}
          onChange={(e) => onChange(e.target.value)}
          // Both variants spelled out in full. Tailwind scans source for
          // COMPLETE class strings, so interpolating onto the end of one
          // ("accent-riot-gold-500${...}") makes the scanner read it as a
          // single unknown candidate and emit no rule at all — which drops
          // accent-color entirely and hands the slider back to the browser
          // default (blue), losing the brand gold.
          className={
            isUnset ? "flex-1 accent-riot-gold-500 opacity-50" : "flex-1 accent-riot-gold-500"
          }
        />
        <span className="w-24 shrink-0 text-right font-mono text-sm tabular-nums text-gray-700 dark:text-riot-navy-100">
          {isUnset ? (
            <span className="text-gray-400 dark:text-riot-navy-400">Not set</span>
          ) : (
            <>
              {value}
              {field.max !== undefined ? (
                <span className="text-gray-400 dark:text-riot-navy-400"> / {field.max}</span>
              ) : null}
            </>
          )}
        </span>
      </div>
    );
  }
  if (field.kind === "number") {
    return (
      <input
        id={id}
        type="number"
        min={field.min}
        max={field.max}
        step={field.step ?? "any"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className={inputClass}
      />
    );
  }
  return (
    <input
      id={id}
      type={field.kind === "phone" ? "tel" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      // On a text field max is the longest the backend accepts.
      maxLength={field.kind === "text" ? field.max : undefined}
      placeholder={field.placeholder}
      className={inputClass}
    />
  );
}

export interface ConfigFieldsFormProps {
  fields: DeviceTypeFieldDefinition[];
  values: ConfigValues;
  onChange: (path: string, value: string) => void;
  idPrefix?: string;
  // Opt-in: when supplied, each section header carries a "Reset to defaults"
  // control. Omitted on the device-create form, where there is no stored
  // configuration to reset — the form already opens on the defaults.
  onResetSection?: (section: string, fields: DeviceTypeFieldDefinition[]) => void;
  // Opt-in: what the device last reported for each path, in the same string
  // form as `values`. Where the staged value differs, the field shows
  // "On device: X" so an operator can see what a save would change.
  deviceValues?: ConfigValues;
  // Opt-in: render bounded number fields as plain number inputs rather than
  // sliders. For ranges a slider cannot hit precisely (ports, seconds).
  preferNumberInput?: boolean;
  // Opt-in: hide fields that do not apply given the other values (e.g. MQTT
  // settings while the protocol is TCP). Hidden fields keep their values.
  isFieldVisible?: (field: DeviceTypeFieldDefinition, values: ConfigValues) => boolean;
  // Opt-in: show the device default on the description line ("… · Default: X")
  // instead of as a separate line, for long forms where every field has one.
  compactHints?: boolean;
  // What the API says about each secret field (set / pending), by path.
  secretStates?: Record<string, SecretFieldState>;
  // Whether the viewer may reveal a secret's value. Defaults to false: a secret
  // is masked with no Reveal unless the page grants it (tenant_admin and above,
  // and only when the API sent the values).
  canRevealSecrets?: boolean;
  // Stored secret values by path, for a page that keeps them out of `values`
  // (so a save never resends them). Shown read-only on Reveal, and only when
  // canRevealSecrets is set.
  storedSecretValues?: ConfigValues;
}

export function ConfigFieldsForm({
  fields,
  values,
  onChange,
  idPrefix = "device-config",
  onResetSection,
  deviceValues,
  preferNumberInput = false,
  isFieldVisible,
  compactHints = false,
  secretStates,
  canRevealSecrets = false,
  storedSecretValues,
}: ConfigFieldsFormProps) {
  // Arrays of objects have their own editor (ObjectArrayField); they cannot be
  // rendered as a single flat control.
  const sections = groupFieldsBySection(fields.filter((field) => field.kind !== "object_array"));
  if (sections.length === 0) return null;

  return (
    <div className="space-y-4">
      {sections.map(({ section, fields: sectionFields }) => (
        <section key={section} className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-riot-navy-300">{section}</h4>
            {onResetSection && (
              <button
                type="button"
                onClick={() => onResetSection(section, sectionFields)}
                className="text-[11px] font-medium text-riot-gold-600 hover:underline dark:text-riot-gold-400"
              >
                Reset to defaults
              </button>
            )}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
            {sectionFields.filter((field) => !isFieldVisible || isFieldVisible(field, values)).map((field) => {
              const id = configFieldId(idPrefix, field.path);
              const value = values[field.path] ?? "";
              const requiredMark = field.required ? <span className="ml-1 text-riot-gold-600 dark:text-riot-gold-400">*</span> : null;
              return (
                <div key={field.path} className={isFullWidth(field) ? "md:col-span-2" : ""}>
                  {field.kind === "boolean" ? (
                    <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-riot-navy-200">
                      <input
                        id={id}
                        type="checkbox"
                        checked={value === "true"}
                        onChange={(e) => onChange(field.path, e.target.checked ? "true" : "false")}
                        className="h-4 w-4 rounded border-gray-300 text-riot-gold-500 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-950"
                      />
                      <span>
                        {field.label}
                        {requiredMark}
                      </span>
                    </label>
                  ) : (
                    <>
                      {/* The day checkboxes are a group with no single
                          control for a label to point at; it names itself. */}
                      {field.kind === "number" && field.format === "weekday_mask" ? (
                        <span id={`${id}-label`} className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                          {field.label}
                          {requiredMark}
                        </span>
                      ) : (
                        <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                          {field.label}
                          {requiredMark}
                        </label>
                      )}
                      <FieldControl
                        field={field}
                        id={id}
                        value={value}
                        onChange={(v) => onChange(field.path, v)}
                        preferNumberInput={preferNumberInput}
                        secretState={secretStates?.[field.path]}
                        canRevealSecrets={canRevealSecrets}
                        storedSecret={storedSecretValues?.[field.path]}
                      />
                    </>
                  )}
                  {field.description && (
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-riot-navy-400">
                      {field.description}
                      {compactHints && deviceDefaultLabel(field) && (
                        <span className="text-gray-400 dark:text-riot-navy-500"> · Default: {deviceDefaultLabel(field)}</span>
                      )}
                    </p>
                  )}
                  {/* What the SELECTED choice actually does. An operator picking
                      a call-handling policy should not have to infer it. */}
                  {isChoiceSelect(field) &&
                    (() => {
                      const selected = field.choices!.find((c) => c.value === value);
                      return selected?.description ? (
                        <p className="mt-1 text-[11px] text-gray-600 dark:text-riot-navy-300">{selected.description}</p>
                      ) : null;
                    })()}
                  {/* An empty field means the device runs its compiled-in value,
                      which is otherwise invisible from here. */}
                  {deviceDefaultLabel(field) && !(compactHints && field.description) && (
                    <p className="mt-1 text-[11px] italic text-gray-500 dark:text-riot-navy-400">
                      Device default: {deviceDefaultLabel(field)}
                    </p>
                  )}
                  {/* A secret's own control says whether it is on the device;
                      its value is never listed. */}
                  {field.kind !== "secret" && deviceValues && deviceValues[field.path] !== undefined && deviceValues[field.path] !== value && (
                    <p
                      className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-400"
                      data-testid={`${id}-on-device`}
                    >
                      On device: {describeConfigValue(field, deviceValues[field.path])}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
